import React, { useEffect, useState } from "react";
import { ResourceInventorySchema, InventorySchema, ResourceType, ResourceSchema, ResourceOptionsPropsType, TrainingInventorySchema, ResourceBookingSchema } from "../data/types";

import { AddCircle } from "@mui/icons-material";

import { ProductFilterBox } from "../components/FilterBox";
import { AddProductModal, DeleteConfirmationModal, InventoryDetailsModal, UpdateProductModal } from "./AppModal";
import { capitalizeString, getDirectLinkFromGoogleDriveSharedLink, getInventoryDescriptionPreview, getInventoryQuantityUnitLabel } from "../utils/helpers";
import {
  getInventoryProductCardStockBadgeClass,
  getInventoryStockStatusForItem,
  getInventoryStockStatusLabel,
} from "../utils/helpers/inventoryStockStatus";
import { useResourceHook } from "../hooks/resourcehooks";
import { useLoadResourceBookings } from "../hooks/memberhooks";
import moment from "moment";
import LoadingScreen from "../pages/shared/LoadingScreen";
import { ReactComponent as InventoryItemIcon } from "../icons/InventoryItemIcon.svg";
import { ReactComponent as InventoryIcon } from "../icons/InventoryIcon.svg";
import { ReactComponent as DeleteIcon } from "../icons/DeleteIcon.svg";
// import { ReactComponent as EditOutlineIcon } from "../icons/EditOutlineIcon.svg";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
// import { ReactComponent as VisibilityOutlineIcon } from "../icons/VisibilityOutlineIcon.svg";



type ResourceProductDetailsProps = {
    resource: ResourceSchema
    // currentResource: ResourceSchema
    resourceType: ResourceType
    updateResourceData?: (resource: ResourceSchema, resourceOptionTypes?: ResourceOptionsPropsType) => void;
    // setResource: React.Dispatch<React.SetStateAction<ResourceSchema>>
    // handleReloadResource: () => void
}
  
export const ResourceProductDetails: React.FC<ResourceProductDetailsProps> = ({resource, resourceType}) => {
    // Move hooks to top
    const resourceInventory =  (resource && resource?.resource_inventory) ? resource.resource_inventory : {} as ResourceInventorySchema
    const resourceInventoryItems = resourceInventory['items'] as InventorySchema[]
    const [currentInventoryList, setCurrentInventoryList] = useState<InventorySchema[]>(resourceInventoryItems ? resourceInventoryItems : [])
    const resourceSchedulesList = resourceInventory['schedule'] as TrainingInventorySchema[]
    const [currentTrainingList, setCurrentTrainingList] = useState<TrainingInventorySchema[]>(resourceSchedulesList ? resourceSchedulesList : [])
    const [isAddingProduct, setIsAddingProduct] = useState<boolean>(false)
    var productType = "item"
    if (["training", "expert"].includes(resourceType)){
        productType = "schedule"
    }
    useEffect(() => {
        setCurrentInventoryList(resourceInventoryItems ? resourceInventoryItems : [])
        setCurrentTrainingList(resourceSchedulesList ? resourceSchedulesList : [])
    }, [resourceInventoryItems, resourceSchedulesList])

    if (!resource || !resource.id) {
        return <LoadingScreen/>;
    }

    // resource = resource ? resource : currentResource
    // const [resource, setResource] = useState<ResourceSchema>(currentResource)

    return (
    <>
        <div className="flex flex-row bg-white border-[1px] border-[#E9E9E9] py-2 w-full">
            <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
                Add your individual {productType}s here, including names, prices, and images.
            </p>  
            <button 
                className="mx-[24px] text-[15px] font-raleway ml-auto font-medium flex flex-row h-[44px]  border-[#E9E9E9] border-0 text-white hover:text-gray-200 px-[24px] py-[12px] bg-primary-200 hover:bg-primary-300 rounded-lg my-auto"
                onClick={()=> setIsAddingProduct(true)}
            >
                <AddCircle className="m-auto mx-1" style={{height: 20, width: 20}} />
                <p className="m-auto">Add {capitalizeString(productType)}</p>
            </button>
        </div>
        <ProductFilterBox />

        {
            (["training", "expert"].includes(resourceType)) ? (
                <>
                    <ProductScheduleList 
                        resource={resource} 
                        currentTrainingList={currentTrainingList}
                        setCurrentTrainingList={setCurrentTrainingList}
                    />
                    <AddProductModal 
                        resourceType={resourceType} 
                        resourceId={resource?.id? resource?.id : ""}
                        setIsOpen={setIsAddingProduct} 
                        isOpen={isAddingProduct} 
                        setCurrentInventoryList={setCurrentTrainingList}
                        // handleReloadResource={handleReloadResource}
                        closeAppModal={()=>setIsAddingProduct(false)}
                    />
                </>
            ) : (
                <>
                    <ProductInventoryList 
                        resource={resource} 
                        currentInventoryList={currentInventoryList} 
                        setCurrentInventoryList={setCurrentInventoryList}
                        setIsAddingProduct={setIsAddingProduct}
                        productType={productType}
                    />

                    <AddProductModal 
                        resourceType={resourceType} 
                        resourceId={resource?.id? resource?.id : ""}
                        setIsOpen={setIsAddingProduct} 
                        isOpen={isAddingProduct} 
                        setCurrentInventoryList={setCurrentInventoryList}
                        // handleReloadResource={handleReloadResource}
                        closeAppModal={()=>setIsAddingProduct(false)}
                    />
                </>
            )
        }


    </>
    )
}



type ProductInventoryListProps = {
    resource: ResourceSchema
    currentInventoryList: InventorySchema[]
    setCurrentInventoryList: React.Dispatch<React.SetStateAction<InventorySchema[]>>
    setIsAddingProduct: React.Dispatch<React.SetStateAction<boolean>>
    productType: string
}

type InventoryProductCardDescriptionProps = {
    description?: string;
    onSeeMore: () => void;
};

const InventoryProductCardDescription: React.FC<InventoryProductCardDescriptionProps> = ({
    description,
    onSeeMore,
}) => {
    const { preview, isTruncated } = getInventoryDescriptionPreview(description);

    return (
        <div className="inventory-product-card-description-wrap" data-testid="inventory-product-card-description-wrap">
            <p className="inventory-product-card-description" data-testid="inventory-product-card-description">
                {preview}
            </p>
            {isTruncated && (
                <button
                    type="button"
                    className="inventory-product-card-description-more"
                    onClick={onSeeMore}
                    data-testid="inventory-product-card-see-more"
                >
                    See more
                </button>
            )}
        </div>
    );
};

type InventoryProductCardImageProps = {
    imageUrl?: string;
    itemName: string;
    isOutOfStock?: boolean;
};

const InventoryProductCardImage: React.FC<InventoryProductCardImageProps> = ({
    imageUrl,
    itemName,
    isOutOfStock = false,
}) => {
    const [hasError, setHasError] = React.useState(false);
    const resolvedUrl = getDirectLinkFromGoogleDriveSharedLink(imageUrl);

    React.useEffect(() => {
        setHasError(false);
    }, [imageUrl]);

    if (!resolvedUrl || hasError) {
        return (
            <div
                className={`inventory-product-card-image inventory-product-card-image-placeholder${
                    isOutOfStock ? " inventory-product-card-image-muted" : ""
                }`}
                data-testid="inventory-product-card-image-placeholder"
                role="img"
                aria-label={`${itemName} image placeholder`}
            >
                <InventoryIcon className="inventory-product-card-image-icon" aria-hidden />
            </div>
        );
    }

    return (
        <img
            className={`inventory-product-card-image${
                isOutOfStock ? " inventory-product-card-image-muted" : ""
            }`}
            src={resolvedUrl}
            alt={itemName}
            data-testid="inventory-product-card-image"
            onError={() => setHasError(true)}
        />
    );
};

type InventoryProductCardStockBadgeProps = {
    inventory: Pick<InventorySchema, "id" | "total_quantity" | "available_quantity" | "is_reusable">;
    bookings?: ResourceBookingSchema[];
};

const InventoryProductCardStockBadge: React.FC<InventoryProductCardStockBadgeProps> = ({ inventory, bookings }) => {
    const stockInput = {
        total_quantity: inventory.total_quantity,
        available_quantity: inventory.available_quantity,
        is_reusable: inventory.is_reusable,
        inventoryId: inventory.id,
        bookings,
    };
    const stockStatus = getInventoryStockStatusForItem(stockInput);
    const badgeClass = getInventoryProductCardStockBadgeClass(stockInput);
    const label = getInventoryStockStatusLabel(stockInput);

    return (
    <span
        className={`inventory-product-card-stock-badge inventory-product-card-stock-badge-${badgeClass}`}
        data-testid="inventory-product-card-stock-badge"
        data-status={stockStatus}
    >
        {label}
    </span>
    );
};

const ProductInventoryList: React.FC<ProductInventoryListProps> = ({resource, currentInventoryList, setCurrentInventoryList, setIsAddingProduct, productType}) => {
    // Move hooks to top
    const resourceInventory =  (resource && resource?.resource_inventory) ? resource.resource_inventory : {} as ResourceInventorySchema
    const resourceInventoryItems = resourceInventory['items'] as InventorySchema[]
    const { deleteInventoryItem } = useResourceHook({resourceId: resource?.id})
    const { resourceBookings } = useLoadResourceBookings(resource?.id || "")
    const [isDeleting, setIsDeleting] = useState<boolean>(false)
    const [product, setProduct] = useState<InventorySchema>()
    const [isViewing, setIsViewing] = useState<boolean>(false)
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [editingItemId, setEditingItemId] = useState<string>("")
    useEffect(() => {
        setCurrentInventoryList(resourceInventoryItems ? resourceInventoryItems : [])
    }, [setCurrentInventoryList, resourceInventoryItems])
    // Guard after hooks
    if (!resource || !resource.id) {
        return <div className="text-red-500 p-4">Resource not found or invalid.</div>;
    }

    const handleDelete = (itemId: string) => {
        if (resource && resource?.id && itemId){
            deleteInventoryItem(resource.id, itemId)
        }
        setProduct(undefined)
        setIsDeleting(false)
    }

    return (
        <>
        <div className="inventory-product-grid">
        {
            (currentInventoryList.length > 0) ? currentInventoryList.map((inventory, i) => {
                const stockInput = {
                    total_quantity: inventory.total_quantity,
                    available_quantity: inventory.available_quantity,
                    is_reusable: inventory.is_reusable,
                    inventoryId: inventory.id,
                    bookings: resourceBookings,
                };
                const stockStatus = getInventoryStockStatusForItem(stockInput);
                const isOutOfStock = stockStatus === "out_of_stock";

                return (
                <div
                    key={inventory.id ?? i}
                    className={`inventory-product-card${
                        isOutOfStock ? " inventory-product-card-out-of-stock" : ""
                    }`}
                    data-testid="inventory-product-card"
                    data-stock-status={stockStatus}
                >
                    <div className="inventory-product-card-image-wrap p-2">
                        <InventoryProductCardImage
                            imageUrl={inventory.image_url}
                            itemName={inventory.item_name}
                            isOutOfStock={isOutOfStock}
                        />
                        <InventoryProductCardStockBadge inventory={inventory} bookings={resourceBookings} />
                    </div>
                    <div className="inventory-product-card-body">
                        <p className="inventory-product-card-title">{inventory.item_name}</p>
                        <InventoryProductCardDescription
                            description={inventory.item_description}
                            onSeeMore={() => {
                                setProduct(inventory);
                                setIsViewing(true);
                            }}
                        />
                        <div className="inventory-product-card-meta">
                            <p className="shrink-0 font-raleway my-auto font-medium">{inventory.total_quantity} {getInventoryQuantityUnitLabel(inventory.measure_unit, inventory.total_quantity)}</p>
                            <p className="text-right font-open-sans text-[#4D4D4D]/80 text-[15px] my-auto">${inventory.unit_price ? inventory.unit_price.toFixed(2) : 0.00} per {inventory.measure_unit}</p>
                        </div>
                    </div>
                    <div className="inventory-product-card-actions">
                        <button
                            type="button"
                            onClick={() => {
                                setIsDeleting(true)
                                setProduct(inventory)
                            }}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-0.5 whitespace-nowrap px-1 py-1.5 text-[11px] font-semibold text-white hover:text-red-400 sm:gap-1 sm:text-xs"
                        >
                            <DeleteIcon className="shrink-0" style={{ height: 14, width: 14 }} />
                            Delete
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setEditingItemId(inventory?.id || "");
                                setIsEditing(true)
                            }}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-0.5 whitespace-nowrap rounded-sm border-1 bg-white px-1 py-1.5 text-[11px] font-semibold text-primary-200/80 hover:text-primary-300 shadow-sm transition-colors hover:bg-[#EFF8FA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white sm:gap-1 sm:px-1.5 sm:text-xs"
                        >
                            <EditIcon className="shrink-0 text-gray-400/80" style={{ height: 13, width: 13 }} aria-hidden="true" />
                            Edit
                        </button>
                        <button
                            type="button"
                            onClick={() => { setProduct(inventory); setIsViewing(true) }}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-0.5 whitespace-nowrap rounded-sm border-0 bg-white px-1 py-1.5 text-[11px] font-semibold text-primary-200/80 hover:text-primary-300 shadow-sm transition-colors hover:bg-[#EFF8FA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white sm:gap-1 sm:px-1.5 sm:text-xs"
                        >
                            <VisibilityIcon className="shrink-0 text-gray-400/80 hover:text-gray-400" style={{ height: 13, width: 13 }} aria-hidden="true" />
                            Details
                        </button>
                    </div>
                </div>
                );
            }) : (
                <div className="col-span-full flex items-center justify-center py-12">
                    <div className="bg-white rounded-xl shadow-md p-12 max-w-md mx-auto">
                        {/* Icon */}
                        <div className="flex justify-center mb-6">
                            <div className="bg-[#EFF8FA] rounded-full p-4">
                                <InventoryItemIcon className="w-12 h-12 text-[#5DB6CE]" />
                            </div>
                        </div>
                        
                        {/* Heading */}
                        <h3 className="text-center text-2xl font-semibold text-primary-200 mb-3 font-raleway">
                            Your inventory is looking a little empty 👀
                        </h3>
                        
                        {/* Body text */}
                        <p className="text-center text-[#333333] mb-8 font-open-sans">
                            Start by adding {productType}s to track your kitchen tools and ingredients.
                        </p>
                        
                        {/* Button */}
                        <button 
                            onClick={() => setIsAddingProduct(true)}
                            className="w-full bg-primary-200 text-white rounded-lg py-3 px-6 font-raleway font-medium flex items-center justify-center gap-2 hover:bg-[#0D47A1] transition-colors"
                        >
                            <AddCircle className="m-auto mx-1" style={{height: 20, width: 20}} />
                            <p className="my-auto text-white text-[16px] font-raleway font-semibold">Add {capitalizeString(productType)}</p>
                        </button>
                    </div>
                </div>
            )
        }
        </div>

        <DeleteConfirmationModal  isOpen={isDeleting} data={product} dataName="inventory item" handleDelete={handleDelete} closeDeleteModal={()=>setIsDeleting(false)} />
        <InventoryDetailsModal resourceDetails={resource} isOpen={isViewing} inventory={product} closeAppModal={()=> setIsViewing(false)} />
        <UpdateProductModal 
            resourceType={resource?.resource_type || "kitchen"} 
            resourceId={resource?.id || ""}
            setIsOpen={setIsEditing} 
            isOpen={isEditing} 
            setCurrentInventoryList={setCurrentInventoryList}
            closeAppModal={()=>{
                setIsEditing(false);
                setEditingItemId("");
            }}
            itemId={editingItemId}
        />
        </>
    )
}



type ProductScheduleListProps = {
    resource: ResourceSchema
    currentTrainingList: TrainingInventorySchema[]
    setCurrentTrainingList: React.Dispatch<React.SetStateAction<TrainingInventorySchema[]>>
}

const ProductScheduleList: React.FC<ProductScheduleListProps> = ({resource, currentTrainingList, setCurrentTrainingList}) => {
    // Move hooks to top
    const resourceInventory =  (resource && resource?.resource_inventory) ? resource.resource_inventory : {} as ResourceInventorySchema
    const resourceSchedulesList = resourceInventory['schedule'] as TrainingInventorySchema[]
    const { deleteInventoryItem } = useResourceHook({resourceId: resource?.id})
    const [isDeleting, setIsDeleting] = useState<boolean>(false)
    const [product, setProduct] = useState<TrainingInventorySchema>()
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [editingItemId, setEditingItemId] = useState<string>("")
    useEffect(() => {
        setCurrentTrainingList(resourceSchedulesList ? resourceSchedulesList : [])
    }, [resourceSchedulesList, setCurrentTrainingList])
    // Guard after hooks
    if (!resource || !resource.id) {
        return <div className="text-red-500 p-4">Resource not found or invalid.</div>;
    }

    const handleDelete = (itemId: string) => {
        if (resource && resource?.id && itemId){
            deleteInventoryItem(resource.id, itemId)
        }
        setProduct(undefined)
        setIsDeleting(false)
    }

    return (
        <>
            <div className="grid w-full items-start gap-4 p-4 pb-6 font-open-sans grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
            {
                (currentTrainingList.length > 0) && currentTrainingList
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((training, i) => (
                    <div key={i} className="flex min-w-0 w-full flex-col overflow-hidden rounded-lg border border-[#E9E9E9] bg-white shadow-sm">
                        <div className="p-2">
                        {
                            (() => {
                                // Backwards compatibility: if bgPhotos exists, use first element; otherwise check bgPhoto (singular)
                                const bgPhotoUrl = resource.bgPhotos?.[0] || resource.bgPhoto;
                                return getDirectLinkFromGoogleDriveSharedLink(bgPhotoUrl) ? (
                                    <img className="h-[152px] w-full rounded-lg object-cover" src={getDirectLinkFromGoogleDriveSharedLink(bgPhotoUrl)} alt={training?.date} /> 
                                ) : (
                                    <div className="h-[152px] w-full rounded-lg bg-[#D9D9D9]/70" />
                                );
                            })()
                        }
                        </div>
                        <div className='flex flex-col'>
                            <div className='flex flex-col mx-4 font-raleway text-[18px] text-black font-semibold'>
                                <b>{(resource.resource_type === 'training') ? 'Training' : 'Session'} Date</b> 
                                <p className="font-medium">{moment(training.date).format('ddd MMM DD, YYYY')}</p>
                            </div>
                            <span className="font-normal text-[15px] my-auto w-11/12 mx-4 text-[#4D4D4D]/80">{training.duration} hour{training.duration !== 1 ? 's' : ''}</span> 
                            <div className='flex flex-row font-open-sans border-t-[1px] border-b-[1px] border-[#E9E9E9] h-auto my-4 w-11/12 mx-auto'>
                                <div className="grid grid-cols-2">
                                    {
                                        training.times?.map((time, i)=> (
                                            <p key={i} className='flex flex-col text-center my-auto mr-6'>
                                                {/* <b>Option {i+1}</b> */}
                                                <span className="p-1 bg-primary-300 rounded-md text-white my-1">{moment(time, 'HH:mm').format('hh:mm a')}</span>
                                            </p>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-row bg-primary-200 h-[75px] font-raleway rounded-b-lg w-full mt-auto'>
                            <p className="my-auto text-white mx-4">
                                <span className="font-semibold">{training.total_quantity || 0}</span> {training.total_quantity !== 1 ? 'people max' : 'person max'}
                            </p>
                            <div className="flex flex-row ml-auto my-auto m-2">
                                <button onClick={()=>{
                                    setIsDeleting(true)
                                    setProduct(training)
                                    }} className="bg-primary-200 text-white text-[16px] font-raleway font-semibold p-2 m-2 ">
                                Delete
                                </button>
                                <button onClick={()=>{ 
                                    setEditingItemId(training?.id || ""); 
                                    setIsEditing(true) 
                                }} className="text-primary-200 bg-white text-[16px] font-raleway font-semibold p-2 m-2 rounded-md ">
                                    Edit
                                </button>
                            </div>
                        </div>
                    </div>
                    
                ))
            }
            </div>

            <DeleteConfirmationModal isOpen={isDeleting} data={product} dataName="schedule" handleDelete={handleDelete} closeDeleteModal={()=>setIsDeleting(false)} />
            <UpdateProductModal 
                resourceType={resource?.resource_type || "training"} 
                resourceId={resource?.id || ""}
                setIsOpen={setIsEditing} 
                isOpen={isEditing} 
                setCurrentInventoryList={setCurrentTrainingList}
                closeAppModal={()=>{
                    setIsEditing(false);
                    setEditingItemId("");
                }}
                itemId={editingItemId}
            />
        </>
    )
}